import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BirthInput from "./BirthInputLegacy";
import AuthModal from "../components/AuthModal";
import LanguagePrompt from "../components/LanguagePrompt";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { supabase } from "../supabaseClient";
import { AnalyticsManager } from "../utils/analytics";
import { AnalyticsEvents } from "../utils/AnalyticsEvents";
import { setAuthSource } from "../utils/authSource";
import {
  IconHeartFilled,
  IconCircleCheckFilled,
} from "@tabler/icons-react";
import {
  saveCompatDraft,
  takeCompatDraft,
  clearCompatDraft,
  type CompatDraft,
  type DraftSide,
} from "../hooks/useCompatMatchDraft";
import { Birth, runGunMilan } from "../utils/calculatorApi";
import "../components/calculators/VedicCalculator.css";
import "./KundliMatchLegacy.css";

interface Koot {
  key: string;
  label: string;
  obtained: number;
  max: number;
  meaning?: string;
  boy?: string;
  girl?: string;
}

interface GunMilan {
  total: number;
  out_of: number;
  koots: Koot[];
}

type Saved = DraftSide;

type Who = "boy" | "girl";

/* The engine labels grahamaitri "Rasi AdhiPathi" and bhakoot "Rasi", which are
   accurate but not what anyone searching for kundli matching recognises. Match
   on the stable key and fall back to the engine's own label if it ever sends a
   koot we have not seen. */
const KOOT_NAMES: Record<string, string> = {
  varna: "Varna",
  vasya: "Vashya",
  vashya: "Vashya",
  tara: "Tara",
  yoni: "Yoni",
  grahamaitri: "Graha Maitri",
  gana: "Gana",
  bhakoot: "Bhakoot",
  nadi: "Nadi",
};

/* Gender comes from the role, not from a question. Several kootas are scored
   directionally from groom to bride, so the two slots are already gendered by
   definition and asking again would be noise. */
const PROFILE_GENDER: Record<Who, string> = { boy: "Male", girl: "Female" };

const titleCase = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const verdictFor = (total: number, max: number) => {
  const pct = (total / max) * 100;
  if (pct >= 86) return "Excellent match";
  if (pct >= 69) return "Very good match";
  if (pct >= 50) return "Workable match";
  return "Needs a closer look";
};

const readableDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

/* Declared at module scope on purpose. When this lived inside KundliMatch it
   was a new component type on every render, so React unmounted and remounted
   both panels whenever either one changed, wiping whatever the user had
   half-typed into the other side. */
const Side: React.FC<{
  label: string;
  /** Decorative only - kept OUT of `label` because label also feeds the
      name input's placeholder, where an emoji does not belong. */
  emoji?: string;
  hint: string;
  data: Saved | null;
  name: string;
  onNameChange: (v: string) => void;
  onSave: (b: Birth, place: string, timezone?: string) => void;
  onClear: () => void;
}> = ({ label, emoji, hint, data, name, onNameChange, onSave, onClear }) => {
  // The name lives here rather than inside BirthInput because BirthInput is
  // shared with the single-chart calculators, which have nobody to name.
  const [nameError, setNameError] = useState("");

  // BirthInput has already validated date, time and place by the time this
  // runs, so the only thing left to check is the one field it does not own.
  const guard = (b: Birth, place: string, timezone?: string) => {
    if (!name.trim()) {
      setNameError("Please add a name so we know whose chart this is.");
      return;
    }
    setNameError("");
    onSave(b, place, timezone);
  };

  return (
    <div className="lkm-side">
      {/* No emoji here: the step rail above already carries it, and twice on
          one screen reads as decoration. */}
      <h3 className="lkm-side-title">{label}&rsquo;s details</h3>
      <p className="lkm-side-hint">{hint}</p>
      {data ? (
        <div className="lkm-done">
          <p className="lkm-done-name">{data.name}</p>
          <p className="lkm-done-line">
            <strong>{readableDate(data.birth.date)}</strong> at{" "}
            <strong>{data.birth.time}</strong>
          </p>
          <p className="lkm-done-place">{data.place}</p>
          <button type="button" className="lkm-change" onClick={onClear}>
            Change details
          </button>
        </div>
      ) : (
        <>
          <label className="lbi-field lkm-name">
            <span>Name</span>
            <input
              type="text"
              value={name}
              placeholder={`${label}'s name`}
              autoComplete="off"
              maxLength={40}
              onChange={(e) => {
                onNameChange(e.target.value);
                if (nameError) setNameError("");
              }}
            />
          </label>
          {nameError ? <p className="lbi-error">{nameError}</p> : null}
          {/* "Confirm", not "Save". English overloads save (store vs rescue) and
              translation engines pick rescue - the Hindi build rendered
              "Save groom" as "दूल्हे को बचाओ", i.e. rescue the groom. Confirm has
              one meaning everywhere. The name is dropped because the 👨/👩
              heading directly above already says whose card this is, and it
              pairs with the "Change details" button that replaces it. */}
          {/* No per-side Confirm. Two confirm buttons for one action is a step
              the visitor should not have to take: the details commit as soon as
              date, time and place are all present. */}
          <BirthInput
            onSubmit={guard}
            cta="Confirm details"
            autoCommit
            recommitKey={name}
          />
        </>
      )}
    </div>
  );
};

/* Three steps: save the groom, save the bride, then match. The scoring is the
   engine's own gun_milan, not ours. Several kootas are directional, so which
   birth goes in which slot changes the answer and the labels have to be
   unmissable.

   The score sits behind a phone number. That gate is also the lead capture:
   the source rides along with OTP verification and the backend stamps it on the
   new user, which is how marketing gets the phone list without a new table. */
const KundliMatch: React.FC = () => {
  const {
    user,
    pricingVariant,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    effectiveBalance,
  } = useAuth();
  const { prices, formatPrice } = usePricing();
  const navigate = useNavigate();

  /* Same resolution CompatibilityScreen uses, so the price in this popup and
     the price the server actually charges cannot drift apart. Note there is no
     `compatibility_report` row in service_prices today, so this resolves via
     report_premium (INR 10000 = Rs 100 after formatPrice's /100). */
  const reportCost =
    prices["compatibility_report"] || prices["report_premium"] || 10;
  const canAfford = effectiveBalance !== null && effectiveBalance >= reportCost;

  const [payOpen, setPayOpen] = useState(false);
  /* Which side is on screen. Completing one hands the visitor to the other
     instead of making them find the tab. */
  const [side, setSide] = useState<Who>("boy");
  /* Any manual move cancels a queued auto-advance, so the screen can never
     slide out from under a tap. */
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelAdvance = useCallback(() => {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }, []);
  const goSide = useCallback(
    (who: Who) => {
      cancelAdvance();
      setSide(who);
    },
    [cancelAdvance],
  );
  const queueAdvance = useCallback(
    (who: Who) => {
      cancelAdvance();
      advanceTimer.current = setTimeout(() => setSide(who), 800);
    },
    [cancelAdvance],
  );
  useEffect(() => cancelAdvance, [cancelAdvance]);

  const [boy, setBoy] = useState<Saved | null>(null);
  const [girl, setGirl] = useState<Saved | null>(null);
  // Kept in the parent for the same reason Side is declared at module scope:
  // so typing in one panel cannot wipe what is half-entered in the other.
  const [boyName, setBoyName] = useState("");
  const [girlName, setGirlName] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const [match, setMatch] = useState<GunMilan | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);

  // Guards the post-login resume against StrictMode's double effect, which
  // would otherwise fire the scoring call twice on one return.
  const resumedRef = useRef(false);
  // Fired once per completed spread so re-renders do not re-report the step.
  const readyReportedRef = useRef(false);

  const score = useCallback(async (b: Saved, g: Saved) => {
    setBusy(true);
    setFailed("");
    try {
      const result = await runGunMilan<GunMilan>(b.birth, g.birth);
      setMatch(result);
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcScoreViewed, {
        total: result.total,
        out_of: result.out_of,
      });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : "Could not read those charts.");
    } finally {
      setBusy(false);
    }
  }, []);

  const save =
    (who: Who) => (birth: Birth, place: string, timezone?: string) => {
      setFailed("");
      setMatch(null);
      const name = (who === "boy" ? boyName : girlName).trim();
      const sideData: Saved = { birth, place, timezone, name };
      /* Hand them to the empty side once this one is complete, but not
         instantly: picking a city is the last field, and switching on the same
         tick pulls the screen away while they are still reading what they
         entered. A short beat lets the summary card land and the step tick
         turn green first, so the move reads as a consequence rather than a
         glitch. */
      if (who === "boy") {
        setBoy(sideData);
        if (!girl) queueAdvance("girl");
      } else {
        setGirl(sideData);
        if (!boy) queueAdvance("boy");
      }
    };

  const clear = (who: Who) => () => {
    setFailed("");
    setMatch(null);
    readyReportedRef.current = false;
    if (who === "boy") setBoy(null);
    else setGirl(null);
  };

  const ready = Boolean(boy && girl);

  useEffect(() => {
    if (ready && !readyReportedRef.current) {
      readyReportedRef.current = true;
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcReady, {});
    }
  }, [ready]);

  /* Coming back from login. The draft is single-use, so a remount cannot replay
     the same match twice. */
  useEffect(() => {
    if (!user || resumedRef.current) return;
    const draft = takeCompatDraft();
    if (!draft) return;
    resumedRef.current = true;
    setBoy(draft.boy);
    setGirl(draft.girl);
    setBoyName(draft.boy.name || "");
    setGirlName(draft.girl.name || "");

    // This is the lead: a phone number verified because the visitor wanted the
    // score. Meta gets it as the STANDARD `Lead` event rather than a custom one,
    // because campaign optimisation and the Ads Manager lead columns only read
    // standard events — a trackCustom here would be invisible to the people
    // running the campaign.
    AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcLead, {
      source: "compatibility_check",
    });
    try {
      const fbq = (window as any).fbq;
      if (fbq) fbq("track", "Lead", { content_name: "compatibility_check" });
    } catch {
      /* pixel blocked; the custom event above still went out */
    }

    void score(draft.boy, draft.girl);
  }, [user, score]);

  const run = () => {
    if (!boy || !girl || busy) return;

    if (!user) {
      // Park the draft BEFORE opening the modal. Google sign-in is a full page
      // reload, so without this the visitor fills in two complete sets of birth
      // details, signs in, and lands back on an empty calculator — at the exact
      // moment they have just handed over their phone number.
      saveCompatDraft({ boy, girl } as CompatDraft);
      setAuthSource("compatibility_check");
      AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcGateShown, {});
      setAuthOpen(true);
      return;
    }
    void score(boy, girl);
  };

  /* Turn the two births into real profiles and hand off to the compatibility
     chat.

     Mirrors ChatPage's own partner path (user_profiles -> user_birth_details,
     then its endpoint), deliberately: that is the path already proven in
     production. What this does NOT have to do is geocode. ChatPage warns at
     length that its in-chat partners arrive as free text with no coordinates,
     which produced profiles that could never generate a chart. Here the places
     came from the autocomplete, so latitude, longitude and zone are already
     known and that whole failure mode does not exist. */
  const openReport = async () => {
    if (!user || openingChat) return;
    setOpeningChat(true);
    setFailed("");
    AnalyticsManager.trackEvent(AnalyticsEvents.CompatCalcChatClicked, {});

    const created: string[] = [];
    try {
      const makeProfile = async (who: Who, side: Saved) => {
        const { data: profile, error: pErr } = await supabase
          .from("user_profiles")
          .insert({ user_id: user.id, name: side.name })
          .select()
          .single();
        if (pErr) throw new Error(pErr.message || "Could not save the profile.");
        created.push(profile.id);

        const { error: bErr } = await supabase
          .from("user_birth_details")
          .insert({
            profile_id: profile.id,
            date_of_birth: side.birth.date,
            time_of_birth: side.birth.time,
            birth_place: side.place,
            birth_lat: String(side.birth.lat),
            birth_lng: String(side.birth.lon),
            birth_timezone: side.timezone || "Asia/Kolkata",
            // Not optional: the chart generator reads it, and coordinates with
            // a NULL offset produce no chart at all.
            timezone_offset: side.birth.tz,
            gender: PROFILE_GENDER[who],
          });
        if (bErr) throw new Error(bErr.message || "Could not save birth details.");
        return profile.id as string;
      };

      const boyId = await makeProfile("boy", boy!);
      const girlId = await makeProfile("girl", girl!);

      // Link the pair in compatibility_profiles the way ChatPage's
      // handleUseExistingPartner does: partner_user_profile_id points at a real
      // user_profiles row that already has a chart, which is what the compat
      // report and the in-chat compat function both validate against.
      // Non-fatal on failure — the report screen can still be handed the pair
      // through navigation state, and the compat function auto-links server
      // side.
      let compatRowId: string | null = null;
      try {
        const { data: link } = await supabase
          .from("compatibility_profiles")
          .insert({
            user_id: user.id,
            source_profile_id: boyId,
            partner_user_profile_id: girlId,
            partner_name: girl!.name,
            partner_gender: PROFILE_GENDER.girl,
            date_of_birth: girl!.birth.date,
            time_of_birth: girl!.birth.time,
            birth_place: girl!.place,
            birth_lat: String(girl!.birth.lat),
            birth_lng: String(girl!.birth.lon),
            birth_timezone: girl!.timezone || "Asia/Kolkata",
          })
          .select()
          .single();
        compatRowId = link?.id ?? null;
      } catch {
        /* link is an optimisation, not a requirement */
      }

      // Pin the partner now, so the chat CTA on the REPORT screen has nothing
      // left to set up — ChatPage restores this key for whichever profile is
      // selected and runs in compatibility mode from there.
      localStorage.setItem(
        `aura_partner_${boyId}`,
        JSON.stringify({ id: girlId, name: girl!.name }),
      );
      // The report screen reads its own side from here (option A: the pair the
      // visitor just matched becomes their selected profile).
      localStorage.setItem("selectedProfileId", boyId);
      clearCompatDraft();

      // Everything the report needs to show both people WITHOUT asking again.
      navigate("/compatibility-report", {
        state: {
          fromCalculator: true,
          // Paid for at the popup, so the report screen should generate on
          // arrival rather than showing another button. The ACTUAL charge is
          // still the server-side hold in handleGenerateReport - this flag only
          // says "the visitor already agreed to the price".
          autoGenerate: true,
          sourceProfileId: boyId,
          partnerProfileId: compatRowId,
          partnerUserProfileId: girlId,
          partnerName: girl!.name,
          partnerBirth: {
            date_of_birth: girl!.birth.date,
            time_of_birth: girl!.birth.time,
            birth_place: girl!.place,
            gender: PROFILE_GENDER.girl,
          },
        },
      });
    } catch (e) {
      // Half-created profiles would show up in their dashboard as nameless
      // duplicates on the next attempt, so undo what landed.
      for (const id of created) {
        await supabase.from("user_profiles").delete().eq("id", id);
      }
      setFailed(
        e instanceof Error ? e.message : "Could not open the report right now.",
      );
      setOpeningChat(false);
    }
  };

  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp],
  );
  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );
  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        countryCode?: string;
      }) =>
        verifyMsg91Otp({ ...payload, variant: pricingVariant || "control" }),
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  return (
    <div className="vedcalc">
      {/* Kundli matching draws a lot of non-English traffic, and the only
          language control until now was a two-letter chip in the navbar. */}
      <LanguagePrompt message="Prefer Hindi or another language? You can read this page, and your match result, in your own language." />

      {/* One card, one side at a time. Two full stacked panels each with its own
          Confirm made this the longest screen in the product on a phone.
          Both panels stay MOUNTED and the inactive one is hidden, because
          BirthInput holds date, time and place in its own state: unmounting it
          would throw away whatever the visitor had half-typed on the other
          side. */}
      <div className="lkm-card">
        {/* One control doing two jobs: it says where you are AND how far
            through. A tab strip only answered the first, so nothing on screen
            told a first-timer that this was three steps rather than an
            open-ended form. Steps 1 and 2 switch sides; step 3 is a
            destination, not a button. */}
        <ol className="lkm-steps" aria-label="Progress">
          {([
            { key: "boy", n: 1, icon: "👨", label: "Male", done: Boolean(boy) },
            { key: "girl", n: 2, icon: "👩", label: "Female", done: Boolean(girl) },
            { key: "match", n: 3, icon: "✨", label: "Match", done: Boolean(match) },
          ] as const).map((st, idx) => {
            const isSide = st.key !== "match";
            const active = isSide ? side === st.key : ready && !match;
            return (
              <li
                key={st.key}
                className={
                  "lkm-step" +
                  (st.done ? " is-done" : "") +
                  (active ? " is-active" : "")
                }
                aria-current={active ? "step" : undefined}
              >
                {idx > 0 ? (
                  <span className="lkm-step__bar" aria-hidden="true" />
                ) : null}
                <button
                  type="button"
                  className="lkm-step__hit"
                  disabled={!isSide}
                  onClick={() => isSide && goSide(st.key as Who)}
                >
                  <span className="lkm-step__dot" aria-hidden="true">
                    {st.done ? "✓" : st.n}
                  </span>
                  <span className="lkm-step__label">
                    <span aria-hidden="true">{st.icon}</span> {st.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        {/* Both panels live in the SAME grid cell: they stay mounted (so a
            half-typed birth time survives a switch) and the inactive one is
            moved out of reach rather than removed, which is what lets the swap
            animate. The cell takes the height of the taller panel, so the card
            does not jump as you move between them. */}
        <div className="lkm-stage" data-side={side}>
          <div
            className={
              "lkm-panel" + (side === "boy" ? " is-on" : " is-off is-left")
            }
            aria-hidden={side !== "boy"}
          >
            <Side
              label="Male"
              emoji="👨"
              hint="His name and birth details"
              data={boy}
              name={boyName}
              onNameChange={setBoyName}
              onSave={save("boy")}
              onClear={clear("boy")}
            />
          </div>
          <div
            className={
              "lkm-panel" + (side === "girl" ? " is-on" : " is-off is-right")
            }
            aria-hidden={side !== "girl"}
          >
            <Side
              label="Female"
              emoji="👩"
              hint="Her name and birth details"
              data={girl}
              name={girlName}
              onNameChange={setGirlName}
              onSave={save("girl")}
              onClear={clear("girl")}
            />
          </div>
        </div>

        {/* Explicit way forward and back. The stepper is tappable, but a first
            timer looks for a Next, not a numbered dot. */}
        <div className="lkm-nav">
          <button
            type="button"
            className="lkm-nav__btn"
            onClick={() => goSide("boy")}
            disabled={side === "boy"}
          >
            <span aria-hidden="true">←</span> Male
          </button>
          <span className="lkm-nav__pos" aria-live="polite">
            {side === "boy" ? "Step 1 of 2" : "Step 2 of 2"}
          </span>
          <button
            type="button"
            className="lkm-nav__btn lkm-nav__btn--next"
            onClick={() => goSide("girl")}
            disabled={side === "girl"}
          >
            Female <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <div className="lkm-run">
        <button
          type="button"
          className="lkm-generate"
          onClick={run}
          disabled={!ready || busy}
        >
          {busy ? "Reading both charts…" : "Match the charts"}
        </button>
        {!ready ? (
          <p className="lkm-hint">
            Confirm both sets of birth details and this will turn on.
          </p>
        ) : !user ? (
          <p className="lkm-hint">
            We will send a one-time code to your number, then show the score.
          </p>
        ) : null}
        {/* A visitor who paid, read the report and pressed back had no way to
            find it again. Not tied to having just matched: they arrive to an
            empty calculator and still need the route back. */}
        {user ? (
          <button
            type="button"
            className="lkm-saved-link"
            onClick={() => navigate("/saved-reports")}
          >
            View saved reports
          </button>
        ) : null}
      </div>


      {failed ? (
        <div className="vedcalc-result vedcalc-failed" role="status">
          <p>{failed}</p>
        </div>
      ) : null}

      {match ? (
        <div className="vedcalc-result" role="status">
          <div className="lkm-score">
            <span className="lkm-total">
              {match.total}
              <span>/{match.out_of}</span>
            </span>
            <span className="lkm-verdict">
              {verdictFor(match.total, match.out_of)}
            </span>
          </div>
          <div className="lkm-bar">
            <span
              style={{ width: `${(match.total / match.out_of) * 100}%` }}
            />
          </div>

          <table className="lkm-table">
            <thead>
              <tr>
                <th>Koota</th>
                <th>Score</th>
                <th>What it covers</th>
              </tr>
            </thead>
            <tbody>
              {match.koots.map((k) => (
                <tr key={k.key} className={k.obtained === 0 ? "is-zero" : ""}>
                  <td>{KOOT_NAMES[k.key] || k.label}</td>
                  <td>
                    {k.obtained}/{k.max}
                  </td>
                  <td>
                    {k.boy && k.girl ? (
                      <span className="lkm-pair">
                        {titleCase(k.boy)} and {titleCase(k.girl)}.{" "}
                      </span>
                    ) : null}
                    {k.meaning}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Teaser: the note sits behind a blur with the gradient CTA
              centred on top, so the detail reads as "there is more here" and
              the tap goes through to chat. Once blurred the copy is
              decorative, hence aria-hidden and inert - the button carries the
              real action and the real label. */}
          {/* The opening lines stay readable so the score still teaches
              something on its own. Everything after that is blurred with the
              unlock CTA centred on it, which is what makes the rest feel
              worth opening. The blurred copy is decorative once obscured,
              hence aria-hidden and inert - the button carries the action. */}
          <p className="vedcalc-note lkm-note-lead">
            18 out of 36 is the traditional threshold for a match to be
            considered workable, and 28 and above is treated as excellent.
          </p>

          <div className="lkm-teaser">
            <p className="vedcalc-note lkm-teaser-text" aria-hidden="true">
              Several kootas are scored in one direction, from the groom to the
              bride, which is why the two charts are not interchangeable. A
              zero on Bhakoot or Nadi is what astrologers look at hardest,
              though both have recognised cancellations that depend on the full
              charts.
            </p>

            <div className="lkm-teaser-overlay">
              <button
                type="button"
                className="vedcalc-cta lkm-cta"
                onClick={() => setPayOpen(true)}
                disabled={openingChat}
              >
                <span aria-hidden="true">🔓</span>{" "}
                {openingChat ? "Opening both charts…" : "Unlock deeper analysis"}
              </button>
            </div>
          </div>

        </div>
      ) : null}

      {/* Pay confirmation. Deliberately a PRE-CHECK, not a payment: the coins
          are taken server-side by place_report_hold_compat inside
          handleGenerateReport, which reserves on start and only settles on a
          successful report. Nothing here debits anything. If the balance is
          short we send them to /wallet rather than half-starting a flow that
          the server would refuse a moment later. */}
      {/* Pay confirmation. A PRE-CHECK, not a payment: coins are taken
          server-side by place_report_hold_compat inside handleGenerateReport,
          which reserves on start and settles only on success. Nothing here
          debits anything. Short balance goes to /wallet rather than half
          starting a flow the server would refuse a moment later. */}
      {payOpen && (
        <div
          className="lkm-pay-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lkm-pay-title"
          onClick={() => !openingChat && setPayOpen(false)}
        >
          <div className="lkm-pay" onClick={(e) => e.stopPropagation()}>
            <span className="lkm-pay-emblem" aria-hidden="true">
              <IconHeartFilled size={26} />
            </span>

            <h3 className="lkm-pay-title" id="lkm-pay-title">
              Full compatibility report
            </h3>
            <p className="lkm-pay-sub">
              {boy?.name} and {girl?.name}, read as one chart.
            </p>

            <ul className="lkm-pay-list">
              {[
                "All 36 gunas explained, not just the score",
                "Manglik dosha checked on both sides",
                "The years that matter for this match",
                "Remedies chosen for the two of you",
              ].map((line) => (
                <li key={line}>
                  <IconCircleCheckFilled size={16} aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="lkm-pay-btn"
              disabled={openingChat || effectiveBalance === null}
              onClick={() => {
                if (!canAfford) {
                  setPayOpen(false);
                  navigate("/wallet");
                  return;
                }
                openReport();
              }}
            >
              {openingChat
                ? "Opening your report..."
                : `${formatPrice(reportCost)} \u00b7 Unlock full report`}
            </button>

            <button
              type="button"
              className="lkm-pay-cancel"
              onClick={() => setPayOpen(false)}
              disabled={openingChat}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </div>
  );
};

export default KundliMatch;
